import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { userService } from '../../services/userService';
import { FiAlertCircle, FiCheck } from 'react-icons/fi';
import { logger } from '../../utils/logger';
import { scrollToTop } from '../../utils/scrollHelper';
import FreeSignupBanner from '../../components/common/FreeSignupBanner';
import ReferralCodeDisplay from '../../components/common/ReferralCodeDisplay';

// Styled Components (reused from SignupPaymentPage)
const PageContainer = styled.div`
  padding: 2rem 0;
  min-height: 100vh;
  background-color: #f8f9fa;
`;

const FormContainer = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  margin-bottom: 2rem;
`;

const FormTitle = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  color: var(--dark);
  font-family: 'Quicksand', sans-serif;
  font-weight: 600;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  border-radius: 12px;
  background-color: #fafafa;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border-left: 4px solid var(--primary);
`;

const SectionTitle = styled.h3`
  margin-bottom: 1.5rem;
  color: var(--primary);
  display: flex;
  align-items: center;
  font-family: 'Quicksand', sans-serif;
  
  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 24px;
    background-color: var(--primary);
    margin-right: 12px;
    border-radius: 4px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  
  @media (min-width: 768px) {
    grid-template-columns: ${props => props.columns || '1fr 1fr'};
  }
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  font-family: 'Nunito', sans-serif;
  color: var(--dark);
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  transition: border-color 0.3s ease, background-color 0.3s ease;
  font-family: 'Nunito', sans-serif;
  
  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(233, 64, 87, 0.1);
  }
  
  ${props => props.error && `
    border-color: #D13046;
    background-color: rgba(209, 48, 70, 0.02);
  `}
`;

const ErrorMessage = styled.div`
  color: #D13046;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Nunito', sans-serif;
`;

const SuccessMessage = styled.div`
  color: #28a745;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Nunito', sans-serif;
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  font-family: 'Quicksand', sans-serif;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(233, 64, 87, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;
  margin-right: 0.5rem;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const TermsCheckbox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 1.5rem;
  
  input[type="checkbox"] {
    margin-top: 0.25rem;
    width: 18px;
    height: 18px;
    accent-color: var(--primary);
  }
  
  label {
    font-size: 0.9rem;
    line-height: 1.5;
    color: #6c757d;
    
    a {
      color: var(--primary);
      text-decoration: none;
      
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const PlanCard = styled.div`
  background: linear-gradient(135deg, #7ECBB9 0%, #65A395 100%);
  color: white;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: left;
`;

const PlanHeader = styled.div`
  margin-bottom: 1.5rem;
  text-align: center;
`;

const PlanTitle = styled.h4`
  font-family: 'Quicksand', sans-serif;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: white;
`;

const PlanPrice = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 0.5rem;
`;

const PriceAmount = styled.span`
  font-size: 2rem;
  font-weight: 700;
  font-family: 'Quicksand', sans-serif;
`;

const PriceNote = styled.span`
  font-size: 0.9rem;
  opacity: 0.8;
  text-decoration: line-through;
`;

const PlanFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
`;

const PlanFeature = styled.li`
  font-family: 'Nunito', sans-serif;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  
  &:before {
    content: '✓';
    color: white;
    font-weight: bold;
    margin-right: 0.5rem;
    flex-shrink: 0;
  }
`;

const StepsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    gap: 2rem;
  }
`;

const Step = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;
`;

const StepNumber = styled.div`
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-family: 'Quicksand', sans-serif;
  flex-shrink: 0;
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.h4`
  font-family: 'Quicksand', sans-serif;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--dark);
`;

const StepDescription = styled.p`
  font-family: 'Nunito', sans-serif;
  font-size: 0.9rem;
  color: #6c757d;
  margin: 0;
  line-height: 1.5;
`;

const FreeSignupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signUp, signIn, signOut } = useAuth();
  const errorRef = useRef(null);
  
  // Extract referral code from URL
  const referralCode = searchParams.get('ref') || 'direct';
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    confirmEmail: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false,
    // Hidden fields for free signup
    plan: 'basic',
    source: referralCode,
    signupType: 'free'
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Update source when referral code changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      source: referralCode
    }));
  }, [referralCode]);
  
  // Scroll to error when errors change
  useEffect(() => {
    if (Object.keys(errors).length > 0 && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [errors]);
  
  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Validate account information
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.confirmEmail.trim()) {
      newErrors.confirmEmail = 'Please confirm your email';
    } else if (formData.email !== formData.confirmEmail) {
      newErrors.confirmEmail = 'Email addresses do not match';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.agreeTerms) {
      newErrors.agreeTerms = 'You must agree to the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    setErrors({});
    
    try {
      logger.info('Starting free signup process', { 
        email: formData.email, 
        source: formData.source,
        signupType: formData.signupType 
      });
      
      // Create user account with Cognito (using only standard attributes)
      const attributes = {
        'given_name': formData.firstName,
        'family_name': formData.lastName,
        'email': formData.email
      };
      
      const result = await signUp(formData.email, formData.password, attributes);
      logger.info('Cognito signup successful', result);
      
      // Try to sign in to get an ID token for the API call
      try {
        const signInResult = await signIn(formData.email, formData.password);
        
        // Save user data to DynamoDB using existing endpoint
        const userData = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          selectedPlan: formData.plan, // Use selectedPlan to match existing API
          email: formData.email,
          source: formData.source,
          signupType: formData.signupType,
          // No payment data for free signups
          paymentMethodId: null,
          paymentId: null
        };
        
        try {
          logger.info('Saving user data to DynamoDB');
          await userService.saveUserSignup(userData, signInResult.idToken);
          logger.info('User data saved to DynamoDB successfully');
        } catch (dbError) {
          logger.error('Error saving user data to DynamoDB during sign-up', dbError);
          // Don't fail the entire signup process if DynamoDB save fails
          // The user is already created in Cognito, which is the most important part
        }
        
        // Keep user logged in and redirect to dashboard (like normal signup)
        navigate('/dashboard');
        
      } catch (signInError) {
        logger.error('Could not sign in after signup to save data', signInError);
        // If sign-in fails, redirect to login page with success message
        setSuccessMessage('Account created successfully! You can now log in with your credentials.');
        setTimeout(() => {
          navigate('/login?message=free-signup-success');
        }, 2000);
      }
      
    } catch (error) {
      logger.error('Free signup error:', error);
      
      let errorMessage = 'An error occurred during signup. Please try again.';
      
      if (error.code === 'UsernameExistsException') {
        errorMessage = 'An account with this email already exists. Please try logging in instead.';
      } else if (error.code === 'InvalidPasswordException') {
        errorMessage = 'Password does not meet requirements. Please choose a stronger password.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <>
      <Helmet>
        <title>Free Sleep Plan Signup - Dozzy</title>
        <meta name="description" content="Create your free Dozzy account and get a personalized sleep plan for your baby at no cost." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <PageContainer>
        <div className="container">
          <FreeSignupBanner />
          
          {/* How It Works Section - moved to top */}
          <FormSection>
            <SectionTitle>How It Works</SectionTitle>
            
            <StepsContainer>
              <Step>
                <StepNumber>1</StepNumber>
                <StepContent>
                  <StepTitle>Create Your Account</StepTitle>
                  <StepDescription>Fill out the form below to create your free account</StepDescription>
                </StepContent>
              </Step>
              
              <Step>
                <StepNumber>2</StepNumber>
                <StepContent>
                  <StepTitle>Add Your Child's Details</StepTitle>
                  <StepDescription>Tell us about your child's age, sleep patterns, and current challenges</StepDescription>
                </StepContent>
              </Step>
              
              <Step>
                <StepNumber>3</StepNumber>
                <StepContent>
                  <StepTitle>Get Your Personalized Plan</StepTitle>
                  <StepDescription>Our AI creates a custom sleep plan based on trusted Australian guidelines</StepDescription>
                </StepContent>
              </Step>
            </StepsContainer>
          </FormSection>
          
          <FormContainer>
            <FormTitle>Create Your Free Account</FormTitle>
            
            <ReferralCodeDisplay referralCode={referralCode !== 'direct' ? referralCode : null} />
            
            {successMessage && (
              <SuccessMessage>
                <FiCheck />
                {successMessage}
              </SuccessMessage>
            )}
            
            {errors.general && (
              <ErrorMessage ref={errorRef}>
                <FiAlertCircle />
                {errors.general}
              </ErrorMessage>
            )}
            
            <form onSubmit={handleSubmit} noValidate>
              {/* Account Information Section */}
              <FormSection>
                <SectionTitle>Account Information</SectionTitle>
                
                <FormRow>
                  <FormGroup>
                    <FormLabel htmlFor="firstName">First Name</FormLabel>
                    <FormInput
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      error={errors.firstName}
                      disabled={isLoading}
                    />
                    {errors.firstName && <ErrorMessage><FiAlertCircle />{errors.firstName}</ErrorMessage>}
                  </FormGroup>
                  
                  <FormGroup>
                    <FormLabel htmlFor="lastName">Last Name</FormLabel>
                    <FormInput
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      error={errors.lastName}
                      disabled={isLoading}
                    />
                    {errors.lastName && <ErrorMessage><FiAlertCircle />{errors.lastName}</ErrorMessage>}
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <FormLabel htmlFor="email">Email Address</FormLabel>
                    <FormInput
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      error={errors.email}
                      disabled={isLoading}
                    />
                    {errors.email && <ErrorMessage><FiAlertCircle />{errors.email}</ErrorMessage>}
                  </FormGroup>
                  
                  <FormGroup>
                    <FormLabel htmlFor="confirmEmail">Confirm Email</FormLabel>
                    <FormInput
                      type="email"
                      id="confirmEmail"
                      name="confirmEmail"
                      value={formData.confirmEmail}
                      onChange={handleChange}
                      error={errors.confirmEmail}
                      disabled={isLoading}
                    />
                    {errors.confirmEmail && <ErrorMessage><FiAlertCircle />{errors.confirmEmail}</ErrorMessage>}
                  </FormGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <FormLabel htmlFor="password">Password</FormLabel>
                    <FormInput
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      error={errors.password}
                      disabled={isLoading}
                    />
                    {errors.password && <ErrorMessage><FiAlertCircle />{errors.password}</ErrorMessage>}
                  </FormGroup>
                  
                  <FormGroup>
                    <FormLabel htmlFor="confirmPassword">Confirm Password</FormLabel>
                    <FormInput
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      error={errors.confirmPassword}
                      disabled={isLoading}
                    />
                    {errors.confirmPassword && <ErrorMessage><FiAlertCircle />{errors.confirmPassword}</ErrorMessage>}
                  </FormGroup>
                </FormRow>
              </FormSection>

              {/* What You'll Get Section */}
              <FormSection>
                <SectionTitle>What You'll Get (Completely Free)</SectionTitle>
                
                <PlanCard>
                  <PlanHeader>
                    <PlanTitle>Free Basic Plan</PlanTitle>
                    <PlanPrice>
                      <PriceAmount>$0</PriceAmount>
                      <PriceNote>(normally $49)</PriceNote>
                    </PlanPrice>
                  </PlanHeader>
                  
                  <PlanFeatures>
                    <PlanFeature>✓ AI-generated personalized sleep plan for one child</PlanFeature>
                    <PlanFeature>✓ Based on your child's specific needs and age</PlanFeature>
                    <PlanFeature>✓ Detailed daily schedule and routines</PlanFeature>
                    <PlanFeature>✓ Bedtime routine recommendations</PlanFeature>
                    <PlanFeature>✓ Night wakings guidance and solutions</PlanFeature>
                    <PlanFeature>✓ Evidence-based advice from Australian experts</PlanFeature>
                    <PlanFeature>✓ Instant access after account creation</PlanFeature>
                  </PlanFeatures>
                </PlanCard>
              </FormSection>

              {/* Terms and Conditions */}
              <TermsCheckbox>
                <input
                  type="checkbox"
                  id="agreeTerms"
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                <label htmlFor="agreeTerms">
                  I agree to the{' '}
                  <Link to="/terms-and-conditions" target="_blank">
                    Terms and Conditions
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy-policy" target="_blank">
                    Privacy Policy
                  </Link>
                </label>
              </TermsCheckbox>
              {errors.agreeTerms && <ErrorMessage><FiAlertCircle />{errors.agreeTerms}</ErrorMessage>}
              
              <SubmitButton type="submit" disabled={isLoading}>
                {isLoading && <LoadingSpinner />}
                {isLoading ? 'Creating Account...' : 'Create Free Account'}
              </SubmitButton>
            </form>
            
            <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
              <p style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                Already have an account?{' '}
                <Link to="/login" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                  Sign in here
                </Link>
              </p>
            </div>
          </FormContainer>
        </div>
      </PageContainer>
    </>
  );
};

export default FreeSignupPage;
